import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Crop,
  FlipHorizontal,
  Image,
  Mic,
  MicOff,
  RefreshCcw,
  Video,
  VideoOff,
  X,
} from 'lucide-react'
import { cn } from '@vc/lib/utils'
import { VirtualBackgroundStream } from '@vc/views/vcLayout/components/video/VirtualBackgroundStream'
import type { BackgroundImage } from '@vc/data/backgroundCategories'
import { useResponsiveDevice } from '@vc/viewmodels/hooks/useResponsiveDevice'
import { Switch } from '@/clientWeb/components/ui/switch'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/clientWeb/components/ui/tooltip'
import { useTranslation } from '@/clientWeb/i18n/LanguageContext'
import { MicLevelIndicator } from './MicLevelIndicator'

interface DevicePreviewProps {
  stream: MediaStream | null
  micLevel: number
  camEnabled: boolean
  micEnabled: boolean
  permissionDenied: boolean
  videoFlipped: boolean
  framingEnabled: boolean
  faceOffset: { x: number; y: number }
  selectedBackground: BackgroundImage | null
  /**
   * Joined through the passive link — no devices are opened for this person, so
   * the mic and camera buttons are removed rather than shown switched off. An
   * "off" button invites a click that would do nothing.
   */
  viewOnly?: boolean
  onToggleMic: () => void
  onToggleCam: () => void
  onToggleFlip: () => void
  onSetFramingEnabled: (enabled: boolean) => void
  onSetFaceOffset: (offset: { x: number; y: number }) => void
  onOpenBackgroundDialog: () => void
}

export function DevicePreview({
  stream,
  micLevel,
  camEnabled,
  micEnabled,
  permissionDenied,
  videoFlipped,
  framingEnabled,
  faceOffset,
  selectedBackground,
  viewOnly = false,
  onToggleMic,
  onToggleCam,
  onToggleFlip,
  onSetFramingEnabled,
  onSetFaceOffset,
  onOpenBackgroundDialog,
}: DevicePreviewProps) {
  const t = useTranslation()
  const { isMobileDevice: isPhoneDevice } = useResponsiveDevice()

  const videoRef = useRef<HTMLVideoElement>(null)
  const framingPopoverRef = useRef<HTMLDivElement>(null)
  const [framingCardOpen, setFramingCardOpen] = useState(false)

  // A callback ref, not an effect keyed on `stream`.
  //
  // This <video> is conditionally rendered: picking a background swaps it for
  // VirtualBackgroundStream, and clearing one mounts a brand new element. An
  // effect with deps [stream] does not re-run then — `stream` never changed —
  // so the new element kept whatever srcObject it was born with, which is none.
  // That is the black preview after removing a background. Tying attachment to
  // the element's existence rather than to unrelated state makes the two
  // impossible to get out of step.
  const attachStream = useCallback(
    (el: HTMLVideoElement | null) => {
      videoRef.current = el
      if (!el) return
      el.srcObject = stream
      if (stream) void el.play().catch(() => {})
    },
    [stream],
  )

  useEffect(() => {
    if (!framingCardOpen) return
    const onClick = (e: MouseEvent) => {
      if (framingPopoverRef.current && !framingPopoverRef.current.contains(e.target as Node)) {
        setFramingCardOpen(false)
      }
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [framingCardOpen])

  const showVideo = camEnabled && !permissionDenied

  return (
    <div className="relative overflow-hidden rounded-2xl bg-black ring-1 ring-black/10">
      {/* Not clickable. A click anywhere on the picture used to flip it too, so
          a stray click — or one to close the framing card — undid a flip the
          user had just chosen. Only the Flip Camera button flips. */}
      <div className="relative" style={{ aspectRatio: '16/9' }}>
        {showVideo ? (
          selectedBackground ? (
            // Flip lives on the wrapper here, not on the media element. With a
            // background applied the plain <video> — which carried the
            // transform — is not rendered at all, so the mirror button did
            // nothing. It has to wrap the whole composite: flipping only the
            // person cutout would mirror them against an unmirrored room.
            <div
              className="h-full w-full transition-transform duration-300"
              style={{ transform: videoFlipped ? 'scaleX(-1)' : 'scaleX(1)' }}
            >
              <VirtualBackgroundStream
                backgroundUrl={selectedBackground.url}
                stream={stream}
                className="h-full w-full object-cover"
                onReady={() => {}}
              />
            </div>
          ) : (
            <video
              ref={attachStream}
              playsInline
              muted
              className="h-full w-full object-cover transition-[transform,object-position] duration-300"
              style={{
                transform: videoFlipped ? 'scaleX(-1)' : 'scaleX(1)',
                objectPosition: framingEnabled
                  ? `${50 + faceOffset.x}% ${50 + faceOffset.y}%`
                  : '50% 50%',
              }}
            />
          )
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <div className="w-20 h-20 rounded-full bg-gray-700 flex items-center justify-center">
              <VideoOff className="w-8 h-8 text-gray-400" />
            </div>
          </div>
        )}
      </div>

      {micEnabled && <MicLevelIndicator micLevel={micLevel} />}

      <div className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-2 sm:gap-3 md:gap-4 bottom-2 sm:bottom-4 md:bottom-6 lg:bottom-10 opacity-100">
        {!viewOnly && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={onToggleMic}
              className={cn(
                'grid h-9 w-9 sm:h-10 sm:w-10 md:h-11 md:w-11 place-items-center cursor-pointer rounded-full ring-1 ring-white/20 transition',
                micEnabled ? 'bg-black/45 hover:bg-black/45' : 'bg-red-600/80 hover:bg-red-600',
              )}
              aria-label={micEnabled ? t('dashboard.mute') : t('dashboard.unmute')}
            >
              {/* Slashed when off, as in the meeting's MicControl. Colour alone
                  left the lobby showing a live-looking mic while muted. */}
              {micEnabled ? (
                <Mic className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
              ) : (
                <MicOff className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
              )}
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" sideOffset={8}>
            {micEnabled ? t('dashboard.mute') : t('dashboard.unmute')}
          </TooltipContent>
        </Tooltip>
        )}

        {!viewOnly && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={onToggleCam}
              className={cn(
                'grid h-9 w-9 sm:h-10 sm:w-10 md:h-11 md:w-11 place-items-center cursor-pointer rounded-full ring-1 ring-white/20 transition',
                camEnabled ? 'bg-black/45 hover:bg-black/45' : 'bg-red-600/80 hover:bg-red-600',
              )}
              aria-label={camEnabled ? t('dashboard.turnCameraOff') : t('dashboard.turnCameraOn')}
            >
              {camEnabled ? (
                <Video className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
              ) : (
                <VideoOff className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
              )}
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" sideOffset={8}>
            {camEnabled ? t('dashboard.turnCameraOff') : t('dashboard.turnCameraOn')}
          </TooltipContent>
        </Tooltip>
        )}

        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={onToggleFlip}
              className="grid h-9 w-9 sm:h-10 sm:w-10 md:h-11 md:w-11 cursor-pointer place-items-center rounded-full bg-black/45 ring-1 ring-white/20 transition hover:bg-black/55"
              aria-label={t('dashboard.flipCamera')}
            >
              <FlipHorizontal className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" sideOffset={8}>
            {t('dashboard.flipCamera')}
          </TooltipContent>
        </Tooltip>

        {!isPhoneDevice && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={onOpenBackgroundDialog}
                className="grid h-9 w-9 sm:h-10 sm:w-10 md:h-11 md:w-11 cursor-pointer place-items-center rounded-full bg-black/45 ring-1 ring-white/20 transition hover:bg-black/55"
                aria-label={t('dashboard.backgroundSettings')}
              >
                <Image className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" sideOffset={8}>
              {t('dashboard.backgroundSettings')}
            </TooltipContent>
          </Tooltip>
        )}

        <div className="relative">
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => setFramingCardOpen((open) => !open)}
                className="grid h-9 w-9 sm:h-10 sm:w-10 md:h-11 md:w-11 cursor-pointer place-items-center rounded-full bg-black/45 ring-1 ring-white/20 transition hover:bg-black/55"
                aria-label={t('dashboard.framing')}
              >
                <Crop className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" sideOffset={8}>
              {t('dashboard.framing')}
            </TooltipContent>
          </Tooltip>

          {framingCardOpen && (
            <div
              className="absolute bottom-full left-1/2 mb-2 w-[240px] sm:w-[280px] -translate-x-1/2 rounded-2xl bg-card p-3 sm:p-4 text-slate-900 dark:text-foreground/80 shadow-xl ring-1 ring-black/10 z-[9999]"
              ref={framingPopoverRef}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-semibold dark:text-foreground flex items-center gap-2">
                    {t('dashboard.framing')}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={framingEnabled}
                    onCheckedChange={(checked) => onSetFramingEnabled(checked)}
                    className="shrink-0"
                    onClick={(e) => e.stopPropagation()}
                  />
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      setFramingCardOpen(false)
                    }}
                    className="flex h-6 w-6 items-center justify-center rounded-full text-slate-400 dark:text-muted-foreground hover:bg-slate-100 dark:hover:bg-muted hover:text-slate-600 dark:hover:text-foreground transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div className="mt-2 text-xs text-slate-500 dark:text-muted-foreground">
                {t('dashboard.centerScreen')}
              </div>

              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  onSetFaceOffset({ x: 0, y: -15 })
                  if (videoRef.current) {
                    videoRef.current.style.transform = 'translate(0,0) scale(1)'
                    videoRef.current.style.objectPosition = 'center center'
                  }
                }}
                className="mt-3 cursor-pointer flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold text-primary hover:bg-primary/10 transition"
              >
                <RefreshCcw className="h-4 w-4" />
                {t('dashboard.reframe')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
